import React from 'react';
import {
  Box, Card, CardContent, Container, Grid, Skeleton, Stack, Switch, Typography,
} from '@mui/material';
import { ChecklistItem, TripRequest, TripRequestStatus } from '@marageti/z4-sdk/lib/travel';
import {
  LabelAndValueElement,
} from '@marageti/z4-lib';
import { TeamMember, Traveler } from '@marageti/z4-sdk/lib/people';
import Alerts from './alerts';
import TravelerCard from '../../components/traveler-card';
import TripRequestTripMatchesContainer from '../../components/trip-request-trip-matches';
import TripRequestAssignToBar from '../../components/trip-request-assign-to-bar';
import TripRequestSubNavigation from '../../components/trip-request-sub-navigation';
import TripRequestMatchingNotes from '../../components/trip-request-matching-notes';
import TravelerProfileNotesSection from '../../components/traveler-profile-notes';
import TripRequestActionBar from '../../components/trip-request-action-bar';
import TripRequestDetails from '../../components/trip-request-details';
import FailureReasonsSummary from '../../components/failure-reasons-summary';
import RemindersSummary from '../../components/reminders-summary';
import TicketsSummary from '../../components/tickets-summary';

type ViewProps = {
  handleSwitchVisability: (e: React.ChangeEvent<HTMLInputElement>, type: 'details' | 'review') => void;
  handleReactivation: () => void;
  handleReenable: () => void;
  handleVerify: () => void;
  isDetailsVisible: boolean;
  isReviewVisible: boolean;
  isReactivating: boolean;
  isReenabling: boolean;
  isSwitchingVisability: boolean;
  isVerifying: boolean;
  loading: boolean
  loggedInTeamMember: TeamMember | null;
  onClickJunk: () => void;
  travelers: Traveler[] | undefined;
  travelerId: string | undefined;
  tripRequest: TripRequest | undefined;
  mutate: () => void;
  onTravelerEdit: () => void;
  refetchTicketCount?: () => void;
};

const TripRequestView = ({
  handleSwitchVisability,
  handleReactivation,
  handleReenable,
  handleVerify,
  isDetailsVisible,
  isReviewVisible,
  isReactivating,
  isVerifying,
  isSwitchingVisability,
  isReenabling,
  loading,
  loggedInTeamMember,
  onClickJunk,
  travelers,
  travelerId,
  tripRequest,
  mutate,
  onTravelerEdit,
  refetchTicketCount,
}: ViewProps) => (
  <>
    <TripRequestAssignToBar />
    <TripRequestSubNavigation />

    <Container maxWidth="lg">
      <Box sx={{
        pt: 4,
        pb: 15,
      }}
      >
        <Grid container spacing={3}>
          <Grid item xs={12} lg={5}>
            {!loading ? (
              <Stack spacing={3}>
                {tripRequest && (
                  <Alerts
                    handleJunk={onClickJunk}
                    handleReactivation={handleReactivation}
                    handleReenable={handleReenable}
                    handleVerify={handleVerify}
                    isReactivating={isReactivating}
                    isReenabling={isReenabling}
                    isVerifying={isVerifying}
                    tripRequest={tripRequest}
                  />
                )}
                <TravelerCard
                  travelers={travelers || []}
                  tripRequest={tripRequest}
                  onEditProfile={onTravelerEdit}
                  onEditEmail={onTravelerEdit}
                />
                {travelerId ? (
                  <TravelerProfileNotesSection travelerId={travelerId} />
                ) : (
                  <Skeleton variant="rounded" height={160} />
                )}
                {tripRequest && (
                  <RemindersSummary
                    tripRequestId={tripRequest.id}
                    travelerId={travelers?.[0]?.id}
                    emptyMessage="There are no reminders for this trip."
                  />
                )}
                {tripRequest
                  && tripRequest.assignedTo === loggedInTeamMember?.id
                  && [
                    TripRequestStatus.PendingStatus,
                    TripRequestStatus.VerifiedStatus,
                  ].includes(tripRequest.status)
                  && (
                    <FailureReasonsSummary
                      tripRequestId={tripRequest.id}
                      isScreened={!tripRequest.checklist?.includes(ChecklistItem.NeedsScreening)}
                    />
                  )}
                {tripRequest && (
                  <TicketsSummary
                    tripRequestId={tripRequest.id}
                    travelerId={travelers?.[0]?.id}
                    travelerName={travelers?.[0]?.fullName}
                    travelerEmail={travelers?.[0]?.email}
                    emptyMessage="There are no tickets for this trip."
                    refetchTicketCount={refetchTicketCount}
                  />
                )}
                <Card variant="loose">
                  <CardContent>
                    <Stack spacing={2}>
                      <LabelAndValueElement
                        key="display-sales-details"
                        title="Display Sales Details"
                        value={(
                          <Box component="span" display="flex" alignItems="flex-start" justifyContent="space-between">
                            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '80%' }} component="span">
                              Allow the traveler to view sales details and the total price reported by the partner
                            </Typography>
                            <Switch
                              onChange={(e) => handleSwitchVisability(e, 'details')}
                              checked={isDetailsVisible}
                              disabled={isSwitchingVisability}
                            />
                          </Box>
                        )}
                      />
                      <LabelAndValueElement
                        key="display-request-review"
                        title="Display Request Review"
                        value={(
                          <Box component="span" display="flex" alignItems="flex-start" justifyContent="space-between">
                            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '80%' }} component="span">
                              Allow the traveler to write a review once this trip has been completed.
                            </Typography>
                            <Switch
                              onChange={(e) => handleSwitchVisability(e, 'review')}
                              checked={isReviewVisible}
                              disabled={isSwitchingVisability}
                            />
                          </Box>
                        )}
                      />
                    </Stack>
                  </CardContent>
                </Card>
              </Stack>
            ) : (<Skeleton variant="rounded" height={296} />)}
          </Grid>
          <Grid item xs={12} lg={7}>
            <Stack spacing={3}>
              {!loading ? (
                <>
                  {tripRequest && (
                    <TripRequestDetails
                      loggedInTeamMember={loggedInTeamMember}
                      tripRequest={tripRequest}
                      onEditSuccess={mutate}
                    />
                  )}
                  <TripRequestTripMatchesContainer
                    tripRequestId={tripRequest?.id}
                    onTripRequestChange={mutate}
                  />
                  <TripRequestMatchingNotes
                    tripRequest={tripRequest}
                    onEditSuccess={mutate}
                  />
                </>
              ) : (
                <>
                  <Skeleton variant="rounded" height={616} />
                  <Skeleton variant="rounded" height={200} />
                  <Skeleton variant="rounded" height={616} />
                </>
              )}
            </Stack>
          </Grid>
        </Grid>

        <TripRequestActionBar tripRequestId={tripRequest?.id} loading={isVerifying} />
      </Box>
    </Container>
  </>
);

export default TripRequestView;
